import React, { useState } from 'react';
import './style.css';

const EmploymentPage = () => {
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(null);
  
  const toggleAccordion = (index) => {
    setActiveAccordion(activeAccordion === index ? null : index);
  };
  
  const openJobDetails = (job) => {
    setSelectedPosition(job);
    // Scroll to top when opening job details
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const closeJobDetails = () => {
    setSelectedPosition(null);
  };

  const jobs = [
    {
      id: 1,
      title: "Java/J2EE Developer",
      type: "Full-time",
      location: "Brussels",
      description: "TechStride Consulting is expanding and actively looking for a Java/J2EE Developer.",
      requirements: [
        "Bachelor's or Master's degree (Engineer, Master) in Computer Science",
        "Minimum 3 years of experience in IT, particularly with Java/J2EE",
        "Good knowledge of Spring Framework, web application development, and Git"
      ],
      tasks: [
        "Implementation of technical solutions",
        "Participation in application design and architecture decisions",
        "Development and maintenance of applications",
        "Unit testing and technical documentation"
      ],
      skills: [
        "Technical knowledge: Java/J2EE, UML, Hibernate, Spring, Oracle, Git",
        "Qualities: Detail-oriented, autonomous, team player with good interpersonal skills"
      ],
      benefits: [
        "Attractive package depending on profile and experience",
        "Work within a human-sized company",
        "Career support and professional training"
      ]
    },
    {
      id: 2,
      title: "Front-End React Developer",
      type: "Full-time",
      location: "Brussels",
      description: "As part of our expansion, we are hiring an experienced Front-End Developer specialized in React.",
      requirements: [
        "Master's degree in Computer Science",
        "3 to 5 years of Front-End development experience",
        "Proficient in React, Redux, HTML5/CSS3 and JavaScript ES6+"
      ],
      tasks: [
        "Design and develop modern, responsive user interfaces",
        "Collaborate with UX/UI designers",
        "Optimize application performance",
        "Testing and debugging"
      ],
      skills: [
        "Technical knowledge: React, Redux, HTML5, CSS3, JavaScript ES6+, Git",
        "Qualities: Creative, meticulous, user-focused"
      ],
      benefits: [
        "Competitive salary based on experience",
        "Innovative and varied projects",
        "Partial remote work possible"
      ]
    },
    {
      id: 3,
      title: "DevOps Consultant",
      type: "Full-time",
      location: "Brussels",
      description: "TechStride Consulting is looking for a DevOps Consultant to support our clients in their digital transformation.",
      requirements: [
        "Master's degree in Computer Science",
        "Minimum 4 years of experience in system administration and DevOps",
        "Proficiency with container technologies (Docker, Kubernetes)"
      ],
      tasks: [
        "Implementation of continuous integration and continuous deployment (CI/CD)",
        "Administration of cloud infrastructures (AWS, Azure)",
        "Automation of deployment processes",
        "Monitoring and performance optimization"
      ],
      skills: [
        "Technical knowledge: Docker, Kubernetes, Jenkins, Git, Ansible, Terraform",
        "Experience with major public clouds (AWS, Azure, GCP)",
        "Qualities: Autonomous, organized, adaptable"
      ],
      benefits: [
        "Attractive compensation based on profile",
        "Ongoing training and certifications",
        "Stimulating and collaborative work environment"
      ]
    },
    {
      id: 4,
      title: "IT Project Manager",
      type: "Full-time",
      location: "Brussels",
      description: "To support our growth, we are seeking an experienced IT Project Manager.",
      requirements: [
        "Master's degree in Computer Science or Business School",
        "5+ years of experience in IT project management",
        "PMP or Prince2 certification appreciated"
      ],
      tasks: [
        "Managing IT projects from A to Z",
        "Leading meetings and coordinating teams",
        "Managing client relationships",
        "Budget tracking and reporting"
      ],
      skills: [
        "Mastery of Agile (Scrum) and traditional methodologies",
        "Excellent communication and leadership skills",
        "Risk management and problem-solving abilities"
      ],
      benefits: [
        "Competitive salary package",
        "Career progression within a growing company",
        "Continuous training and professional development"
      ]
    }
  ];

  const faqItems = [
    {
      question: "How does the recruitment process work?",
      answer: "Our recruitment process typically consists of three steps: an initial interview with our HR team, a technical interview with one of our domain experts, and a final interview with a manager. We respond to candidates within 10 days maximum after each step."
    },
    {
      question: "Do you offer internships or work-study programs?",
      answer: "Yes, we regularly offer internships and work-study contracts. These opportunities are posted on our website and social networks according to our needs. Feel free to send us a spontaneous application if no current offer matches your profile."
    },
    {
      question: "Is remote work possible?",
      answer: "We offer a flexible work policy. Depending on the projects and clients, partial remote work is possible. Specific arrangements are discussed during the recruitment process and adapted to the needs of the project."
    },
    {
      question: "What are the career progression opportunities within the company?",
      answer: "We encourage internal mobility and professional development. Regular evaluations help identify advancement opportunities, whether toward deeper technical expertise or managerial roles. We also provide continuous training to support your growth."
    },
    {
      question: "What social benefits are offered?",
      answer: "We provide a comprehensive benefits package including company health insurance, meal vouchers, contribution to transportation costs, and an employee savings plan. Depending on your role and seniority, additional benefits such as profit-sharing or performance bonuses may apply."
    }
  ];

  return (
    <div className="employment-page">
      {selectedPosition ? (
        <div className="job-details-container">
          <div className="job-details">
            <button className="back-button" onClick={closeJobDetails}>
              <i className="fas fa-arrow-left"></i> Back to jobs
            </button>
            
            <h2>{selectedPosition.title}</h2>
            <div className="job-header-info">
              <span className="job-type">{selectedPosition.type}</span>
              <span className="job-location"><i className="fas fa-map-marker-alt"></i> {selectedPosition.location}</span>
            </div>
            
            <div className="job-section">
              <h3>Job description</h3>
              <p>{selectedPosition.description}</p>
            </div>
            
            <div className="job-section">
              <h3>Candidate profile</h3>
              <ul>
                {selectedPosition.requirements.map((req, index) => (
                  <li key={index}>{req}</li>
                ))}
              </ul>
            </div>
            
            <div className="job-section">
              <h3>Responsibilities</h3>
              <ul>
                {selectedPosition.tasks.map((task, index) => (
                  <li key={index}>{task}</li>
                ))}
              </ul>
            </div>
            
            <div className="job-section">
              <h3>Required skills</h3>
              <ul>
                {selectedPosition.skills.map((skill, index) => (
                  <li key={index}>{skill}</li>
                ))}
              </ul>
            </div>
            
            <div className="job-section">
              <h3>Benefits</h3>
              <ul className="benefits-list">
                {selectedPosition.benefits.map((benefit, index) => (
                  <li key={index}>{benefit}</li>
                ))}
              </ul>
            </div>
            
            <div className="apply-section">
              <h3>Interested in this position?</h3>
              <p>Send us your CV and cover letter.</p>
              <button className="apply-button">Apply now</button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="employment-header">
            <div className="header-overlay">
              <h1>Join our team</h1>
              <p>Explore our job opportunities and grow your career with us</p>
            </div>
          </div>
          
          <div className="employment-content">
            <div className="intro-section">
              <h2>Why join us?</h2>
              <div className="benefits-grid">
                <div className="benefit-card ">
                  <i className="fas fa-rocket benefits-grid-color"></i>
                  <h3>Innovative projects</h3>
                  <p>Work on exciting projects at the cutting edge of technology</p>
                </div>
                <div className="benefit-card">
                  <i className="fas fa-users benefits-grid-color"></i>
                  <h3>Passionate team</h3>
                  <p>Join passionate experts in a collaborative environment</p>
                </div>
                <div className="benefit-card">
                  <i className="fas fa-chart-line benefits-grid-color"></i>
                  <h3>Career growth</h3>
                  <p>Develop your skills with a personalized path</p>
                </div>
                <div className="benefit-card">
                  <i className="fas fa-balance-scale benefits-grid-color"></i>
                  <h3>Work-life balance</h3>
                  <p>Enjoy a flexible organization that respects your balance</p>
                </div>
              </div>
            </div>
            
            <div className="jobs-section">
              <h2>Current openings</h2>
              <div className="jobs-list">
                {jobs.map((job) => (
                  <div className="job-card" key={job.id}>
                    <div className="job-card-content">
                      <h3>{job.title}</h3>
                      <div className="job-info">
                        <span className="job-type">{job.type}</span>
                        <span className="job-location"><i className="fas fa-map-marker-alt"></i> {job.location}</span>
                      </div>
                      <p className="job-description">{job.description}</p>
                    </div>
                    <div className="job-card-footer">
                      <button 
                        className="view-details-button"
                        onClick={() => openJobDetails(job)}
                      >
                        View details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="faq-section">
              <h2>Frequently asked questions</h2>
              <div className="accordion">
                {faqItems.map((item, index) => (
                  <div 
                    className={`accordion-item ${activeAccordion === index ? 'active' : ''}`} 
                    key={index}
                  >
                    <button 
                      className="accordion-title" 
                      onClick={() => toggleAccordion(index)}
                    >
                      {item.question}
                      <i className={`fas ${activeAccordion === index ? 'fa-minus' : 'fa-plus'}`}></i>
                    </button>
                    <div className="accordion-content">
                      <p>{item.answer}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="spontaneous-application">
              <div className="spontaneous-content">
                <h2>Speculative application</h2>
                <p>Can't find a position that matches your profile but would like to join our team? Send us a speculative application!</p>
                <button className="apply-button" onClick={() => window.location.href = '/contact'}>Send my application</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EmploymentPage;